/**
 * ClientSavView — Vue client "Suivi après-vente".
 *
 * ⚠️  Vocabulaire strict côté client :
 *   - JAMAIS "SAV", "litige", "réclamation" ou terme technique.
 *   - "Votre suivi après-vente" · "Votre demande est prise en charge".
 *
 * Réutilise le langage visuel Wyvio existant (bg gris clair, cards premium,
 * bottom nav via ClientTopBar), sans introduire de nouveau design system.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, CheckCircle2, Clock3, Send, MessagesSquare,
  Activity, PartyPopper, Sparkles,
} from "lucide-react";
import ClientTopBar from "@/components/client/ClientTopBar";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatTime, formatFrDate } from "@/lib/format";

const CLIENT_STATUS_TITLES = {
  open:                    "Votre demande est prise en charge",
  analysis_in_progress:    "Analyse en cours",
  solution_in_progress:    "Solution en cours",
  appointment_scheduled:   "Rendez-vous programmé",
  resolved:                "Votre véhicule est prêt",
  closed:                  "Suivi terminé",
};

const ClientSavView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sav, setSav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("suivi");

  const load = async () => {
    try {
      const s = await api.clientGetSav(id);
      setSav(s);
    } catch (e) {
      if (e.status === 401) navigate("/client/login", { replace: true });
      if (e.status === 403 || e.status === 404) navigate("/client", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const handleSend = async () => {
    if (!msgText.trim() || sending || sav?.is_closed) return;
    setSending(true);
    try {
      await api.clientSendSavMessage(id, msgText.trim());
      setMsgText("");
      await load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setSending(false);
    }
  };

  if (loading || !sav) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <Loader2 size={22} strokeWidth={2} className="text-[#111] animate-spin" />
      </div>
    );
  }

  const status = sav.current_status;
  const title = CLIENT_STATUS_TITLES[status] || sav.current_status_label;

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-24" data-testid="page-client-sav-view">
      <ClientTopBar />

      <main className="max-w-md mx-auto px-5 pt-4">
        <Link
          to="/client"
          data-testid="client-sav-back"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] hover:text-[#111]"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Retour au véhicule
        </Link>

        {/* Header premium */}
        <div className="mt-4 rounded-3xl overflow-hidden bg-gradient-to-br from-[#111] to-[#1F2937] text-white p-5 relative">
          <div aria-hidden className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#C8FF2F]/25 blur-3xl" />
          <div className="relative">
            <p className="text-[10.5px] font-medium uppercase tracking-wider text-white/60">
              Suivi après-vente
            </p>
            <h1
              className="mt-1 text-[22px] font-semibold tracking-tight"
              data-testid="client-sav-title"
            >
              {title}
            </h1>
            <p className="mt-1 text-[13px] text-white/70">
              {sav.vehicle?.vehicle_name}
            </p>
            {sav.is_closed && sav.resolution_summary && (
              <div className="mt-4 rounded-2xl bg-white/10 p-3.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
                  Résolution
                </p>
                <p className="mt-1 text-[13px] text-white whitespace-pre-wrap">
                  {sav.resolution_summary}
                </p>
              </div>
            )}
            {!sav.is_closed && (
              <p className="mt-3 text-[12px] text-white/70">
                Ouvert le {formatFrDate(sav.opened_at)}
              </p>
            )}
          </div>
        </div>

        {/* Tabs simples */}
        <div className="mt-5 flex items-center gap-1 p-1 bg-white rounded-full border border-[#EEF2F5] w-fit">
          <button
            type="button"
            data-testid="client-sav-tab-suivi"
            onClick={() => setTab("suivi")}
            className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12px] font-medium transition ${tab === "suivi" ? "bg-[#111] text-white" : "text-[#6B7280]"}`}
          >
            <Activity size={12} strokeWidth={2} />
            Suivi
          </button>
          <button
            type="button"
            data-testid="client-sav-tab-messages"
            onClick={() => setTab("messages")}
            className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12px] font-medium transition ${tab === "messages" ? "bg-[#111] text-white" : "text-[#6B7280]"}`}
          >
            <MessagesSquare size={12} strokeWidth={2} />
            Messages
            {sav.messages?.length > 0 && (
              <span className={`min-w-[16px] h-4 rounded-full text-[10px] font-semibold flex items-center justify-center px-1 ${tab === "messages" ? "bg-[#C8FF2F] text-[#111]" : "bg-[#111] text-white"}`}>
                {sav.messages.length}
              </span>
            )}
          </button>
        </div>

        {tab === "suivi" && (
          <div className="mt-4 space-y-4">
            {/* Étapes visibles client */}
            <div className="rounded-3xl bg-white p-5 border border-[#EEF2F5]" data-testid="client-sav-steps">
              <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6B7280]">
                Étapes de votre demande
              </p>
              <ol className="mt-4 relative">
                <span aria-hidden className="absolute left-[15px] top-2 bottom-2 w-px bg-[#EEF2F5]" />
                {(sav.workflow || []).map((s) => {
                  const done = s.state === "completed";
                  const active = s.state === "in_progress" || s.state === "waiting";
                  return (
                    <li key={s.key} className="relative flex gap-4 py-2.5" data-testid={`client-sav-step-${s.key}`}>
                      <div className={`relative z-10 h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[#DCFCE7] text-[#166534]" : active ? "bg-[#C8FF2F] text-[#111]" : "bg-[#EEF2F5] text-[#9CA3AF]"}`}>
                        {done ? <CheckCircle2 size={13} strokeWidth={2} /> : <Clock3 size={13} strokeWidth={2} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13.5px] ${done || active ? "font-semibold text-[#111]" : "text-[#6B7280]"}`}>
                          {s.title}
                        </p>
                        {s.client_message && (active || done) && (
                          <p className="mt-0.5 text-[11.5px] text-[#6B7280]">
                            {s.client_message}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* Timeline chronologique sanitizée */}
            {sav.timeline?.length > 0 && (
              <div className="rounded-3xl bg-white p-5 border border-[#EEF2F5]" data-testid="client-sav-timeline">
                <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6B7280]">
                  Historique
                </p>
                <ul className="mt-3 space-y-2.5">
                  {sav.timeline.map((e) => (
                    <li key={e.id} className="text-[12.5px] text-[#111] flex items-start gap-2">
                      <Sparkles size={11} strokeWidth={2} className="mt-1 text-[#9CA3AF]" />
                      <div className="flex-1">
                        <p>
                          {e.type === "sav_opened" && "Votre demande a bien été prise en charge."}
                          {e.type === "sav_status_changed" && `Nouvelle étape : ${e.meta?.to?.replace(/_/g, " ")}`}
                          {e.type === "sav_message_sent" && (e.actor?.side === "client" ? "Vous avez envoyé un message." : "Votre agence vous a envoyé un message.")}
                          {e.type === "sav_closed" && "Votre suivi est terminé."}
                        </p>
                        <p className="mt-0.5 text-[10.5px] text-[#9CA3AF]">
                          {formatTime(e.at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-4 rounded-3xl bg-white p-4 border border-[#EEF2F5]" data-testid="client-sav-messages">
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {(sav.messages || []).length === 0 ? (
                <p className="text-center text-[12.5px] text-[#9CA3AF] py-6">
                  Aucun message pour l&apos;instant.
                </p>
              ) : (
                sav.messages.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] ${m.from_side === "client" ? "ml-auto bg-[#111] text-white" : "bg-[#F7F8FA] text-[#111]"}`}
                  >
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className={`mt-1 text-[10px] ${m.from_side === "client" ? "text-white/60" : "text-[#9CA3AF]"}`}>
                      {formatTime(m.created_at)}
                    </p>
                  </div>
                ))
              )}
            </div>
            {!sav.is_closed ? (
              <div className="mt-3 flex items-end gap-2">
                <textarea
                  data-testid="client-sav-msg-input"
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  rows={2}
                  placeholder="Écrire un message à votre agence…"
                  className="flex-1 rounded-2xl border border-[#EEF2F5] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#C8FF2F]/60 resize-none"
                />
                <button
                  type="button"
                  data-testid="client-sav-msg-send"
                  onClick={handleSend}
                  disabled={!msgText.trim() || sending}
                  className="h-10 px-4 rounded-full inline-flex items-center gap-1.5 bg-[#111] text-white text-[12px] font-medium disabled:opacity-50"
                >
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Envoyer
                </button>
              </div>
            ) : (
              <p className="mt-3 text-center text-[11.5px] text-[#9CA3AF]">
                Suivi terminé — messagerie en lecture seule.
              </p>
            )}
          </div>
        )}

        {sav.is_closed && (
          <div className="mt-5 rounded-3xl bg-gradient-to-br from-[#C8FF2F] to-[#B0E625] text-[#111] p-4 flex items-start gap-3" data-testid="client-sav-closed-banner">
            <PartyPopper size={18} strokeWidth={2} />
            <div>
              <p className="text-[13px] font-semibold">Merci pour votre confiance</p>
              <p className="mt-0.5 text-[11.5px]">
                Ce suivi est terminé — vous pouvez toujours le consulter à tout moment.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientSavView;
